import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserNotifications } from '../../ducks/user.duck';
import moment from 'moment';

const isOlderThan14Days = date => {
  return moment().diff(moment(date), 'days') > 14;
};

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const rawTransactions = sortedTransactions(payload.data.data);
      const filteredTransactions = rawTransactions.filter(tx => {
        const lastTransition = tx.attributes?.lastTransition;
        const lastTransitionedAt = tx.attributes?.lastTransitionedAt;

        if (!lastTransitionedAt) return true;

        // 1️⃣ Hide inquiries older than 14 days
        const isInquiry = lastTransition === 'transition/inquire';

        if (isInquiry && isOlderThan14Days(lastTransitionedAt)) {
          return false;
        }

        // 2️⃣ Hide payment failed older than 14 days
        const isPaymentFailed =
          lastTransition === 'transition/payment-failed' ||
          lastTransition === 'transition/expire-payment';

        if (isPaymentFailed && isOlderThan14Days(lastTransitionedAt)) {
          return false;
        }

        return true;
      });

      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(filteredTransactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 100;
const FALLBACK_INBOX_PAGE_SIZE = 20;
const INBOX_TRANSACTION_QUERY_TIMEOUT_MS = 12000;

const parseSearchParams = search => {
  if (!search) return {};
  if (typeof search === 'string') return parse(search);
  if (typeof search === 'object') return search;
  return {};
};

const withTimeout = (promise, timeoutMs, message) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
};

export const loadData = (params, search) => async (dispatch, _getState, sdk) => {
  const { tab } = params;
  const onlyFilter = tab === 'sales' ? 'sale' : tab === 'orders' ? 'order' : null;

  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  try {
    const { page = 1 } = parseSearchParams(search);
    const initialPage = Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;

    const baseParams = {
      only: onlyFilter,
      lastTransitions: getAllTransitionsForEveryProcess(),
      include: [
        'listing',
        'provider',
        'listing.images',
        'provider.profileImage',
        'customer',
        'customer.profileImage',
        'booking',
        'reviews',
      ],
      perPage: INBOX_PAGE_SIZE,
    };

    const queryPage = perPage =>
      withTimeout(
        sdk.transactions.query({
          ...baseParams,
          page: initialPage,
          perPage,
        }),
        INBOX_TRANSACTION_QUERY_TIMEOUT_MS,
        'Inbox transactions query timed out'
      );

    let response;
    try {
      response = await queryPage(INBOX_PAGE_SIZE);
    } catch (error) {
      if (INBOX_PAGE_SIZE > FALLBACK_INBOX_PAGE_SIZE) {
        response = await queryPage(FALLBACK_INBOX_PAGE_SIZE);
      } else {
        throw error;
      }
    }

    dispatch(addMarketplaceEntities(response));
    dispatch(fetchOrdersOrSalesSuccess(response));
    dispatch(fetchCurrentUserNotifications());
    return response;
  } catch (e) {
    dispatch(fetchOrdersOrSalesError(storableError(e)));
    throw e;
  }
};
